import { createRequestFromState, createSearchResults } from './util'
import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

import { AdvancedSearchJobsActions } from './partials/search-jobs'

import { DOWNLOADS_NAMESPACE } from '@/store/downloads'
import { SHARED_NAMESPACE } from '../shared'

export const AdvancedSearchActions = {
  assignIndistinctResults: async ({ commit }, { indistinctResults }) => {
    commit('setIndistinctResults', indistinctResults)
  },
  getEntities: async ({ commit }) => {
    return axiosRequest.
      get('/advanced_search_entities').
      then(({ data }) => {
        commit('setEntities', data.entities || [])
        commit('initAdvancedSearch')
      }).
      catch(handleError)
  },
  selectGroupEntity: async ({ commit }, { group, entity }) => {
    return axiosRequest.
      get(`/advanced_search_properties/${entity}`).
      then(({ data }) => {
        commit('setGroupEntity', { id: group, entity, properties: data })
      })
  },
  newQueryGroup: async ({ state, commit }, { interGroupOperator }) => {
    const nextSequence = Math.max(0, ...state.query_groups.map(g => g.sequence))
    commit('setGroupEntity', { id: nextSequence + 1, entity: null, properties: {} })
    commit('setInterGroupOperator', interGroupOperator)
  },
  delQueryGroup: async ({ commit }, group) => {
    commit('delQueryGroup', group)
  },
  newNestedParam: async ({ commit }, { group, sequence, union }) => {
    commit('setNestedParam', { id: group, sequence, union })
  },
  updateNestedParam: async ({ commit }, { group, sequence, union }) => {
    commit('setNestedParam', { id: group, sequence, union, update: true })
  },
  delNestedParam: async ({ commit }, { group, sequence }) => {
    commit('delNestedParam', { id: group, sequence })
  },
  selectProperty: async ({ commit }, { group, property, sequence }) => {
    // everytime a property is set, we should clear the terms and operator
    commit('setGroupTerm', { id: group, term: null, sequence })
    commit('setGroupOperator', { id: group, operator: null, sequence })
    commit('setGroupProperty', { id: group, property, sequence })
  },
  selectOperator: async ({ state, commit, dispatch }, { group, operator, sequence }) => {
    const queryGroup = state.query_groups.find(g => g.sequence === group)
    const fields = queryGroup.properties.fields.searchable_fields

    let params = queryGroup.entity_params
    if (sequence) params = queryGroup.nested_params.find(p => p.sequence === sequence)

    const field = fields.find(f => f.value === params.field)

    commit('setGroupOperator', { id: group, operator, sequence })
    commit('setGroupOptions', { id: group, options: { model: field?.references, property: field?.value, through: field?.through, type: field?.operator_type }, sequence })

    if (!field?.references) return Promise.resolve()

    let path = `${field?.references}/${field?.value}`
    if (field?.operator_type === 'referenced_string') { path += '_text' }

    return await dispatch(`${SHARED_NAMESPACE}/getDropdownOptions`, { path }, { root: true })
  },
  selectTerms: async ({ commit }, { group, term, sequence }) => {
    commit('setGroupTerm', { id: group, term, sequence })
  },
  selectResultEntity: async ({ commit }, entity) => {
    commit('setResultEntity', entity)
    commit('setSearchResults', createSearchResults())

    return Promise.resolve()
  },
  loadSearchTerms: async ({ commit, dispatch }, { job, results }) => {
    const searchTerms = job.search_params

    commit('setLoadedJob', job)

    await dispatch('selectResultEntity', searchTerms.result_params.entity)
    commit('setSearchResults', results)
    commit('setInterGroupOperator', searchTerms.logical_operator)
    commit('setIndistinctResults', searchTerms.indistinct_results)

    for (const group of searchTerms.query_groups) {
      await dispatch('selectGroupEntity', { group: group.sequence, entity: group.entity_params.entity })
    }

    // Loading Terms and Operators for each Query Group
    for (const group of searchTerms.query_groups) {
      await dispatch('selectProperty', { group: group.sequence, property: group.entity_params.field, sequence: null })
      await dispatch('selectOperator', { group: group.sequence, operator: group.entity_params.operator, sequence: null })
      await dispatch('selectTerms', { group: group.sequence, term: group.entity_params.term, sequence: null })

      for (const nestedParam of group.nested_params) {
        await dispatch('newNestedParam', { group: group.sequence, sequence: nestedParam.sequence, union: nestedParam.logical_operator })
        await dispatch('selectProperty', { group: group.sequence, property: nestedParam.field, sequence: nestedParam.sequence })
        await dispatch('selectOperator', { group: group.sequence, operator: nestedParam.operator, sequence: nestedParam.sequence })
        await dispatch('selectTerms', { group: group.sequence, term: nestedParam.term, sequence: nestedParam.sequence })
      }
    }
  },
  doSearch: async ({ state, commit }) => {
    const request = createRequestFromState(state)
    commit('setSearchLoading', true)
    commit('setSearchResults', createSearchResults())
    commit('setSelectedResults', [])
    commit('setSearchedQueryGroups')

    return axiosRequest.
      post('/advanced_search', request).
      then(({ data }) => {
        commit('setSearchResults', data)
        commit('setSearchLoading', false)
        return null
      }).
      catch(handleError)
  },
  getExportFields: async ({ state, commit, getters }) => {
    const entity = state.entity

    const queryGroups = state.searched_query_groups
    let fields = []

    for (const group of queryGroups) {
      const queryEntity = group.entity_params.entity

      if (entity === queryEntity) continue

      fields.push({
        text: getters.fieldsByGroupId(group.sequence).find(p => p.value === group.entity_params.field)?.text,
        value: `${queryEntity}.${group.entity_params.field}`,
      })

      for (const innerTerm of group.nested_params) {
        fields.push({
          text: getters.fieldsByGroupId(group.sequence).find(p => p.value === innerTerm.field)?.text,
          value: `${queryEntity}.${innerTerm.field}`,
        })
      }
    }

    // unique fields by value
    fields = [...new Map(fields.map(item => [item.value, item])).values()]

    return axiosRequest.
      get(`/advanced_search_properties/${entity}`).
      then(({ data }) => {
        commit('setQueryExportableFields', fields || [])
        commit('setExportableFields', data?.fields?.exportable_fields || [])
      }).
      catch(handleError)
  },
  exportSearchResults: async ({ state, commit }, { selectedColumns, queryColumns }) => {
    const selectedIds = state.selected_results.map(r => r.id)
    const queryGroups = state.searched_query_groups

    const columns = [
      ...selectedColumns,
      ...queryColumns.
        filter(q => selectedColumns.indexOf(q) < 0).
        map(q => `*${q}`),
    ]

    const queryValues = []

    for (const group of queryGroups) {
      const queryEntity = group.entity_params.entity

      queryValues.push({
        field: `${queryEntity}.${group.entity_params.field}`,
        operator: group.entity_params.operator,
        term: group.entity_params.term,
      })

      for (const innerTerm of group.nested_params) {
        queryValues.push({
          field: `${queryEntity}.${innerTerm.field}`,
          operator: innerTerm.operator,
          term: innerTerm.term,
        })
      }
    }

    return axiosRequest.
      post('/export_search_results', {
        ids: selectedIds,
        model: state.entity,
        columns,
        query_values: queryValues,
      }).
      then(({ data }) => commit(`${DOWNLOADS_NAMESPACE}/addDownloadRequest`, data.download_id, { root: true })).
      catch(handleError)
  },

  exportToCSV: async ({ commit }, requestData) => {
    return axiosRequest.
      post('/export_search_results', requestData).
      then(({ data }) => commit(`${DOWNLOADS_NAMESPACE}/addDownloadRequest`, data.download_id, { root: true })).
      catch(handleError)
  },
  generateEmbeddedSearchQuery: async ({ state, commit }) => {
    const request = createRequestFromState(state)
    commit('setEmbeddedSearchQuery', request)
    return Promise.resolve(request)
  },

  ...AdvancedSearchJobsActions,
}
